import { AxesValuProps, ChartAxesFormattedAxes } from '../ChartAxesInterfaces';
import { ChartFilterGroupProps } from '../../../redux/ChartFilterGroup/ChartFilterGroupInterface';
import Logger from '../../../Logger';
import FetchData from '../../ServerCall/FetchData';
import {
  ICalculationStep,
  ICurrentCalculationSession,
  IFlows,
} from '../../../redux/Calculations/CurrentCalculationSessionInterface';
import { TDimensionType } from '../../../types/types';
import { AxisType, DimensionType } from '../../CommonFunctions/aliases';

import { checkIfMissingColumn } from '../../CommonFunctions/CommonFunctions';

/**
 * This function is called in makeServiceCall.ts function.
 * It format the chartAxes into the way it is needed for api call
 */
export const getChartData = async (
  axesValues: AxesValuProps[],
  chartProp: any,
  chartControls: any,
  chartGroup: ChartFilterGroupProps,
  tablesForSelectedDataSets: any,
  dashBoardGroup: any,
  propKey: string,
  screenFrom: string,
  token: string,
  chartType: any,
  setShowSnackBar?: React.Dispatch<React.SetStateAction<boolean>>,
  setSnackBarMessage?: React.Dispatch<React.SetStateAction<string>>,
  forQueryData?: boolean,
  savedCalculations?: any,
  showErrorNotification?: any,
  storeErrorDetails?: any,
  tooltipFormats?: any,
  resetExtraMeasureField?: any,
) => {
  let _chartAxes: any = [];
  const currentOperator =
    chartControls?.properties?.[propKey]?.rowSecurity?.currentOperator.toLowerCase();
  const currentOperatorColumn =
    chartControls?.properties?.[propKey]?.columnSecurity?.currentOperator.toLowerCase();

  const savedCalculatedFieldNames = savedCalculations?.map(
    (item: any) => item.calculationInfo.calculatedFieldName,
  );

  if (chartProp.chartAxes) {
    _chartAxes = chartProp.chartAxes;
  } else {
    _chartAxes = chartProp.properties[propKey].chartAxes;
  }

  /*	PRS 21/07/2022	Construct filter object for service call */
  const getChartLeftFilter = (
    filters: any,
    name: string,
    groupId?: string,
    syncFilter?: boolean,
    anyConditionMatch?: boolean, // it will only be passed for report filter
  ) => {
    let _type: any = {};

    let _chartProp = filters;

    _type.panelName = name;
    _type.shouldAllConditionsMatch =
      anyConditionMatch === true
        ? false
        : anyConditionMatch === false
          ? true
          : !_chartProp.any_condition_match;

    if (groupId) {
      _type.groupId = groupId;
    }
    _type.filters = [];

    /*	To determine filter type	*/
    const _getFilterType = (item: any) => {
      let _type = '';

      if (item.fieldtypeoption === 'Relative Filter') return 'relativeFilter';
      if (item.fieldtypeoption === 'Top N') return 'topn';
      if (item.fieldtypeoption === 'Aggregate Condition') return 'aggr';
      if (
        item.filterTypeTillDate === 'enabled' &&
        item.fieldtypeoption === 'Pick List' &&
        item.exprTypeTillDate
      ) {
        return 'tillDate';
      }

      switch (item.dataType) {
        case 'integer':
        case 'decimal':
          _type = 'number';
          break;
        case 'timestamp':
        case 'date':
          _type = 'date';
          break;
        default:
          _type = 'text';
          break;
      }

      return _type.concat(
        '_',
        item.fieldtypeoption === 'Search Condition' ? 'search' : 'user_selection',
      );
    };

    /*	Set User Selection property */
    const _getUserSelection = (item: any) => {
      Logger('info', 'user selection', item.userSelection ?? 'undefined');
      if (
        item.filterTypeTillDate === 'enabled' &&
        item.fieldtypeoption === 'Pick List' &&
        item.exprTypeTillDate
      ) {
        return syncFilter
          ? item.userSelection?.filter((selection: any) => selection !== '(All)')
          : item.userSelection;
      }
      if (['Aggregate Condition', 'Search Condition'].includes(item.fieldtypeoption)) {
        if (item.exprType === 'blank') return [];
        if (item.exprType === 'between' && (item.greaterThanOrEqualTo || item.lessThanOrEqualTo)) {
          return [item.greaterThanOrEqualTo, item.lessThanOrEqualTo];
        } else if (item.exprInput) {
          return [item.exprInput];
        } else {
          return [''];
        }
      } else if (item.fieldtypeoption === 'Pick List') {
        return item.userSelection?.filter((item: any) => item !== '(All)');
      } else if (item.fieldtypeoption === 'Top N') {
        return [item.exprInput];
      } else return [''];
    };

    /*	Determine whether to add a particular field	*/
    const _getIsFilterValidToAdd = (item: any) => {
      Logger('info', 'item in chart data', item);
      if (!item.fieldtypeoption) {
        return false;
      }

      if (item.fieldtypeoption === 'Pick List') {
        if (item.filterTypeTillDate === 'enabled' && item.exprTypeTillDate) return true;
        else if (item.userSelection) {
          if (
            item.userSelection.length === 0 ||
            (item.userSelection.includes('(All)') && !syncFilter)
          )
            return false;
          else {
            return true;
          }
        }
        return false;
      } else if (['Aggregate Condition', 'Search Condition'].includes(item.fieldtypeoption)) {
        if (
          item.exprType === 'between' &&
          (isInvalidValue(item.greaterThanOrEqualTo) || isInvalidValue(item.lessThanOrEqualTo))
        ) {
          return false;
        } else if (item.exprType === 'blank') return true;
        else if (item.exprType !== 'between' && isInvalidValue(item.exprInput)) {
          return false;
        }
      } else if (item.fieldtypeoption === 'Relative Filter') {
        if (item.expTypeToRelativeDate !== 'current' && !item.exprInputToValueType) return false;
        if (item.expTypeFromRelativeDate !== 'current' && !item.exprInputFromValueType)
          return false;
        if (
          !item.expTypeAnchorDate ||
          (item.expTypeAnchorDate === 'specificDate' && !item.exprInputSpecificDate)
        )
          return false;
        return true;
      } else if (item.fieldtypeoption === 'Top N') {
        if (!item.exprInput || !['ascending', 'descending'].includes(item.exprType)) return false;
        return true;
      } else {
        return false;
      }

      return true;
    };
    const getCalculationInfoByUid = (uid: string) => {
      if (!savedCalculations || !uid) return null;

      return savedCalculations.find((item: any) => item.uuid === uid);
    };
    let _items = [];

    if (_chartProp.fields) _items = _chartProp.fields;
    else _items = _chartProp;
    /*	Iterate through each fields added in the Filter Dropzone	*/

    _items.forEach((item: any) => {
      let _filter: any = {};
      _filter.filterType = _getFilterType(item);
      _filter.tableId = item.tableId;
      _filter.fieldName = item.fieldname;
      _filter.displayName = item.displayname;
      _filter.dataType = item.dataType.toLowerCase();
      _filter.shouldExclude = item.includeexclude === 'Exclude';
      if (item.fieldtypeoption === 'Relative Filter') _filter.shouldExclude = false;

      if (['Search Condition', 'Aggregate Condition'].includes(item.fieldtypeoption)) {
        if (item.exprType) {
          _filter.operator = item.exprType;
          ///For Not Equal To operator
          if (item.exprType === 'notEqualTo') {
            _filter.operator = 'equalTo';
            _filter.shouldExclude = !_filter.shouldExclude;
          }
        } else {
          _filter.operator = item.dataType === 'text' ? 'begins_with' : 'greater_than';
        }
      } else if (item.fieldtypeoption === 'Pick List') {
        _filter.operator = 'in';
      } else if (item.fieldtypeoption === 'Top N') {
        _filter.operator = ['ascending', 'descending'].includes(item.exprType)
          ? item.exprType
          : 'ascending';
      } else {
        _filter.operator = 'between';
      }
      if (_filter.filterType === 'tillDate') {
        _filter.operator = 'in';
      }

      if (item.dataType === 'timestamp' || item.dataType === 'date') {
        _filter.timeGrain = item.prefix;
        // if (_filter.filterType !== "tillDate")
        _filter.isTillDate = item.exprTypeTillDate;
      }
      if (item.fieldtypeoption === 'Relative Filter') {
        _filter.timeGrain = 'date';
        _filter.relativeCondition = {
          from: [item.expTypeFromRelativeDate, item.exprInputFromValueType, item.expTypeFromdate],
          to: [item.expTypeToRelativeDate, item.exprInputToValueType, item.expTypeTodate],
          anchorDate:
            item.expTypeAnchorDate !== 'specificDate'
              ? item.expTypeAnchorDate
              : item.exprInputSpecificDate,
        };
      }

      _filter.userSelection = _getUserSelection(item);
      if (['Top N', 'Aggregate Condition'].includes(item.fieldtypeoption)) {
        _filter.aggr = item.aggr ?? 'sum';
      }
      if (item.SavedCalculationUUID) {
        const calculation = getCalculationInfoByUid(item.SavedCalculationUUID);
        if (calculation) {
          _filter.calculatedField = [
            {
              calculatedFieldName: calculation.name.split(' ').join(' '),
              // TODO: [0] make it dynamic
              isAggregated: calculation.isAggregated,
              fields: calculation.calculationInfo.fields,
              flows: calculation.calculationInfo.flows,
              conditionFilters: calculation.calculationInfo.conditionFilters,
              overrided: calculation.calculationInfo.overrideCount > 0,
            },
          ];
          _filter.isCalculatedField = true;
        }
      }
      if (_getIsFilterValidToAdd(item)) {
        _type.filters.push(_filter);
      }
    });

    return _type;
  };

  const getFormattedAxes = (axesValuesParam: any) => {
    /*	PRS 21/07/2022 */
    let formattedAxes: ChartAxesFormattedAxes = {};
    axesValuesParam.forEach((axis: AxesValuProps) => {
      let dim = '';
      let dimensionType: TDimensionType = 'dim';

      switch (axis.name) {
        case 'Filter':
          dim = 'filters';
          break;

        case 'Dimension':
        case 'Date':
        case 'Row':
        case 'Split By':
        case 'Column':
        case 'Distribution':
          dim = 'dimensions';
          break;

        case 'Location':
          /*	dim = "dims";	*/
          dim = 'dimensions';
          break;

        case 'Measure':
          dim = 'measures';
          break;

        case 'X Axis':
          dim = 'measures';
          break;

        case 'Y Axis':
          dim = 'measures';
          break;
      }
      if (axis.name === AxisType.Row) dimensionType = DimensionType.ROW;
      else if (axis.name === AxisType.Column) dimensionType = DimensionType.COLUMN;
      else if (
        [AxisType.Dimension, AxisType.Location, AxisType.Date, AxisType.Distribution].includes(
          axis.name,
        )
      )
        dimensionType = DimensionType.DIMENSION;
      else if (axis.name === AxisType.SplitBy) dimensionType = DimensionType.SPLIT;

      var formattedFields: any = [];

      axis.fields.forEach((field: any) => {
        var formattedField: any = {
          tableId: field.tableId,
          displayName: field.displayname,
          fieldName: field.fieldname,
          dataType: field.dataType?.toLowerCase(),
          rollupDepth: field.rollupDepth ? true : false,
          measureOrder: field.measureOrder,
          originalDataSetId: field.originalDataSetId,
          originalTableId: field.originalTableId,
          ...(field.SavedCalculationUUID &&
            field.isCalculatedField && {
              SavedCalculationUUID: field.SavedCalculationUUID,
              calculatedField: [
                savedCalculations.find((calc: any) => calc.uuid === field.SavedCalculationUUID)
                  .calculationInfo,
              ],
              isCalculatedField: field.isCalculatedField,
            }),
          SavedCalculationUUID: field.SavedCalculationUUID,
          ...([AxisType.X, AxisType.Y, AxisType.Measure, AxisType.ChartFilter].includes(axis.name)
            ? {}
            : { dimensionType }),
        };
        if (field.rollupDepth !== undefined) {
          formattedField.rollupDepth = field.rollupDepth; // ? true : false
        }

        if (field.dataType === 'date' || field.dataType === 'timestamp') {
          formattedField.timeGrain = field.timeGrain;
        }

        if (dim === 'dimensions' && field.uId) {
          formattedField.dimensionId = field.uId;
        }

        if (axis.name === 'Measure' || axis.name === 'X Axis' || axis.name === 'Y Axis') {
          formattedField.aggr = field.agg;
          formattedField.measureId = field.uId;
          formattedField.originalDataSetId = field.originalDataSetId;
          formattedField.originalTableId = field.originalTableId;

          if (formattedField.aggr === 'agg') formattedField.aggr = 'sum';
          //Updating windowFunction in QueryAPI
          if (field.windowfn) {
            //Function used to convert all the values of windowFunction in camelCase
            function toCamelCase(str: any) {
              return str
                .replace(/(?:^\w|[A-Z]|\b\w)/g, (word: any, index: any) => {
                  return index === 0 ? word.toLowerCase() : word.toUpperCase();
                })
                .replace(/\s+/g, '');
            }

            switch (field.windowfn.windowFnOptions) {
              case 'standing': //If standing gets selected in windowFunction, then below data will be send to API
                //sending windowFn for all charts except richtext
                if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                  if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length === 0) {
                  } else {
                    formattedField.windowFn = [
                      toCamelCase(field.windowfn.windowFnOptions),
                      toCamelCase(field.windowfn.rank),
                      toCamelCase(field.windowfn.order),
                    ];
                  }
                } else {
                  if (
                    !['heatmap', 'crossTab', 'boxPlot', 'bubbleMap', 'richtext'].includes(chartType)
                  ) {
                    if (_chartAxes[1].fields.length === 0) {
                    } else {
                      formattedField.windowFn = [
                        toCamelCase(field.windowfn.windowFnOptions),
                        toCamelCase(field.windowfn.rank),
                        toCamelCase(field.windowfn.order),
                      ];
                    }
                  }
                }

                //sending windowFnMatrix for two dimensional charts
                if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                  if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length === 0) {
                  } else {
                    formattedField.windowFnMatrix = [
                      _chartAxes[1].fields.length,
                      _chartAxes[2].fields.length,
                    ];
                  }
                }

                //sending windowFnPartition for two dimensional charts
                if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                  if (_chartAxes[1].fields.length > 0 && _chartAxes[2].fields.length === 0) {
                    formattedField.windowFnPartition = [field.windowfn.standingRowIndex];
                  } else {
                    if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length > 0) {
                      formattedField.windowFnPartition = [field.windowfn.standingColumnIndex];
                    } else {
                      if (_chartAxes[1].fields.length > 0 && _chartAxes[2].fields.length > 0) {
                        formattedField.windowFnPartition = [
                          field.windowfn.standingRowIndex,
                          field.windowfn.standingColumnIndex,
                        ];
                      } else {
                        if (
                          _chartAxes[1].fields.length === 0 &&
                          _chartAxes[2].fields.length === 0
                        ) {
                        }
                      }
                    }
                  }
                } else {
                  //sending windowFnPartition for one dimensional charts
                  if (
                    !['heatmap', 'crossTab', 'boxPlot', 'bubbleMap', 'richtext'].includes(chartType)
                  ) {
                    if (_chartAxes[1].fields.length === 0) {
                    } else {
                      formattedField.windowFnPartition = [field.windowfn.standingRowIndex];
                    }
                  }
                }

                break;
              case 'sliding': //If sliding gets selected in windowFunction then, below data will be send to API
                //sending windowFn for all charts except richtext
                if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                  if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length === 0) {
                  } else {
                    formattedField.windowFn = [
                      toCamelCase(field.windowfn.windowFnOptions),
                      toCamelCase(field.windowfn.slidingAggregation),
                    ];
                  }
                } else {
                  if (
                    !['heatmap', 'crossTab', 'boxPlot', 'bubbleMap', 'richtext'].includes(chartType)
                  ) {
                    if (_chartAxes[1].fields.length === 0) {
                    } else {
                      formattedField.windowFn = [
                        toCamelCase(field.windowfn.windowFnOptions),
                        toCamelCase(field.windowfn.slidingAggregation),
                      ];
                    }
                  }
                }

                //sending windowFnOption for all charts except richtext
                if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                  if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length === 0) {
                  } else {
                    formattedField.windowFnOption = [field.windowfn.slidingPreInc];
                    if (![undefined, null].includes(field.windowfn.slidingNextInc)) {
                      formattedField.windowFnOption.push(field.windowfn.slidingNextInc);
                    }
                  }
                } else {
                  if (
                    !['heatmap', 'crossTab', 'boxPlot', 'bubbleMap', 'richtext'].includes(chartType)
                  ) {
                    if (_chartAxes[1].fields.length === 0) {
                    } else {
                      formattedField.windowFnOption = [field.windowfn.slidingPreInc];
                      if (![undefined, null].includes(field.windowfn.slidingNextInc))
                        formattedField.windowFnOption.push(field.windowfn.slidingNextInc);
                    }
                  }
                }

                //sending windowFnMatrix for two dimensional charts
                if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                  if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length === 0) {
                  } else {
                    formattedField.windowFnMatrix = [
                      _chartAxes[1].fields.length,
                      _chartAxes[2].fields.length,
                    ];
                  }
                }

                //sending windowFnPartition for two dimensional charts
                if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                  if (_chartAxes[1].fields.length > 0 && _chartAxes[2].fields.length === 0) {
                    formattedField.windowFnPartition = [field.windowfn.slidingRowIndex];
                  } else {
                    if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length > 0) {
                      formattedField.windowFnPartition = [field.windowfn.slidingColumnIndex];
                    } else {
                      if (_chartAxes[1].fields.length > 0 && _chartAxes[2].fields.length > 0) {
                        formattedField.windowFnPartition = [
                          field.windowfn.slidingRowIndex,
                          field.windowfn.slidingColumnIndex,
                          ['rowwise'].includes(field.windowfn.slidingSlideDirection) ? 0 : 1,
                        ];
                      } else {
                        if (
                          _chartAxes[1].fields.length === 0 &&
                          _chartAxes[2].fields.length === 0
                        ) {
                        }
                      }
                    }
                  }
                } else {
                  //sending windowFnPartition for one dimensional charts
                  if (
                    !['heatmap', 'crossTab', 'boxPlot', 'bubbleMap', 'richtext'].includes(chartType)
                  ) {
                    if (_chartAxes[1].fields.length === 0) {
                    } else {
                      formattedField.windowFnPartition = [field.windowfn.slidingRowIndex];
                    }
                  }
                }

                break;
              case 'standingsvssliding': //If standingsvssliding gets selected in windowFunction, then below data will be send to API
                //sending windowFn for all charts except richtext
                if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                  if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length === 0) {
                  } else {
                    formattedField.windowFn = [
                      toCamelCase(field.windowfn.percentage),
                      ['First', 'Last'].includes(field.windowfn.standingSlidingReferenceWn)
                        ? toCamelCase(field.windowfn.standingSlidingReferenceWn)
                        : toCamelCase(field.windowfn.standingSlidingAggregation),
                    ];
                  }
                } else {
                  if (
                    !['heatmap', 'crossTab', 'boxPlot', 'bubbleMap', 'richtext'].includes(chartType)
                  ) {
                    if (_chartAxes[1].fields.length === 0) {
                    } else {
                      formattedField.windowFn = [
                        toCamelCase(field.windowfn.percentage),
                        ['First', 'Last'].includes(field.windowfn.standingSlidingReferenceWn)
                          ? toCamelCase(field.windowfn.standingSlidingReferenceWn)
                          : toCamelCase(field.windowfn.standingSlidingAggregation),
                      ];
                    }
                  }
                }

                //sending windowFnOption for all charts except richtext
                if (field.windowfn.standingSlidingReferenceWn === null) {
                  if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                    if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length === 0) {
                    } else {
                      formattedField.windowFnOption = [field.windowfn.standingSlidingPreInc];
                      if (![undefined, null].includes(field.windowfn.standingSlidingNextInc))
                        formattedField.windowFnOption.push(field.windowfn.standingSlidingNextInc);
                    }
                  } else {
                    if (
                      !['heatmap', 'crossTab', 'boxPlot', 'bubbleMap', 'richtext'].includes(
                        chartType,
                      )
                    ) {
                      if (_chartAxes[1].fields.length === 0) {
                      } else {
                        formattedField.windowFnOption = [field.windowfn.standingSlidingPreInc];
                        if (![undefined, null].includes(field.windowfn.standingSlidingNextInc))
                          formattedField.windowFnOption.push(field.windowfn.standingSlidingNextInc);
                      }
                    }
                  }
                }

                //sending windowFnMatrix for two dimensional charts
                if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                  if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length === 0) {
                  } else {
                    formattedField.windowFnMatrix = [
                      _chartAxes[1].fields.length,
                      _chartAxes[2].fields.length,
                    ];
                  }
                }

                //sending windowFnPartition for two dimensional charts
                if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
                  if (_chartAxes[1].fields.length > 0 && _chartAxes[2].fields.length === 0) {
                    formattedField.windowFnPartition = [field.windowfn.standingSlidingRowIndex];
                  } else {
                    if (_chartAxes[1].fields.length === 0 && _chartAxes[2].fields.length > 0) {
                      formattedField.windowFnPartition = [
                        field.windowfn.standingSlidingColumnIndex,
                      ];
                    } else {
                      if (_chartAxes[1].fields.length > 0 && _chartAxes[2].fields.length > 0) {
                        formattedField.windowFnPartition = [
                          field.windowfn.standingSlidingRowIndex,
                          field.windowfn.standingSlidingColumnIndex,
                          ['rowwise'].includes(field.windowfn.standingSlidingSlideDirection)
                            ? 0
                            : 1,
                        ];
                      } else {
                        if (
                          _chartAxes[1].fields.length === 0 &&
                          _chartAxes[2].fields.length === 0
                        ) {
                        }
                      }
                    }
                  }
                } else {
                  //sending windowFnPartition for one dimensional charts
                  if (
                    !['heatmap', 'crossTab', 'boxPlot', 'bubbleMap', 'richtext'].includes(chartType)
                  ) {
                    if (_chartAxes[1].fields.length === 0) {
                    } else {
                      formattedField.windowFnPartition = [field.windowfn.standingSlidingRowIndex];
                    }
                  }
                }
                break;
            }
          }
        }
        formattedFields.push(formattedField);
      });

      if (dim !== 'filters' && !formattedAxes[dim]) {
        formattedAxes[dim] = formattedFields;
      } else if (dim !== 'filters') {
        formattedAxes[dim] = [...formattedAxes[dim], ...formattedFields];
      }
    });

    if (
      chartType === 'funnel' ||
      chartType === 'gauge' ||
      chartType === 'simplecard' ||
      chartType === 'richText'
    ) {
      formattedAxes.dimensions = [];
    }

    formattedAxes.filterPanels = [];

    /*	PRS 21/07/2022	Get filter object and pushed to request body object	*/

    //let _filterZoneFields = _chartAxes[0].fields; /*	PRS For Override	*/
    let _filterZoneFields = axesValuesParam[0].fields;
    let _hasInvalidFilterData = _filterZoneFields.filter((field: any) => field.isInValidData);

    if (_filterZoneFields.length > 0 && _hasInvalidFilterData && _hasInvalidFilterData.length > 0) {
      Logger('info', 'Filter has invalid data.');
      return;
    }

    const getCalculationInfoByUid = (uid: string) => {
      if (!savedCalculations || !uid) return null;

      return savedCalculations.find((item: any) => item.uuid === uid);
    };

    //let _filterObj = getChartLeftFilter(_chartAxes[0]); /*	PRS 21/07/2022	Get filter object and pushed to request body object	*/
    let _filterObj = getChartLeftFilter(axesValuesParam[0], 'chartFilters');
    if (_filterObj.filters.length > 0) {
      formattedAxes.filterPanels.push(_filterObj);
    }

    if (
      axesValuesParam.find((axes: any) => axes.name === 'Measure')?.fields[0]
        ?.disableReportFilterForOverride
    ) {
      Logger(
        'info',
        'report filter  override',
        axesValuesParam.find((axes: any) => axes.name === 'Measure').fields[0],
      );
    } else {
      if (screenFrom === 'Dashboard') {
        dashBoardGroup.groups.forEach((grp: string) => {
          if (dashBoardGroup.filterGroupTabTiles[grp].includes(propKey)) {
            ////Check this condition 1. group check if cont 2. propkey

            let rightFilterObj = getChartLeftFilter(
              chartGroup.groups[grp].filters,
              'reportFilters',
              grp,
              chartGroup.groups[grp].syncFilter,
              chartGroup.groups[grp].any_condition_match,
            );

            if (rightFilterObj.filters.length > 0) {
              formattedAxes.filterPanels.push(rightFilterObj);
            }
            if (
              chartControls?.properties[propKey]?.rowSecurity?.filterPanels[currentOperator]
                ?.filters.length > 0
            ) {
              const rawFilters =
                chartControls?.properties?.[propKey]?.rowSecurity?.filterPanels?.[currentOperator]
                  ?.filters || [];

              const enhancedFilters = rawFilters.map((filter: any) => {
                const calculation = getCalculationInfoByUid(filter.calculationUUID);
                return {
                  ...filter,
                  ...(calculation && {
                    calculatedField: [
                      {
                        calculatedFieldName: calculation.name.split(' ').join(' '),
                        isAggregated: calculation.isAggregated,
                        fields: calculation.calculationInfo.fields,
                        flows: calculation.calculationInfo.flows,
                        conditionFilters: calculation.calculationInfo.conditionFilters,
                        overrided: calculation.calculationInfo.overrideCount > 0,
                      },
                    ],
                    isCalculatedField: true,
                  }),
                  filterType: `row_level_security_${currentOperator.toLowerCase()}`,
                  operator: 'in',
                  shouldExclude: false,
                  calculation,
                };
              });

              const payload = {
                filters: enhancedFilters,
                panelName: 'rowLevelSecurityFilters',
                shouldAllConditionsMatch: true,
              };
              if (
                ['crossTab', 'table'].includes(chartType) &&
                chartProp.chartAxes?.[1]?.fields.length > 0
              ) {
                formattedAxes.filterPanels.push(payload);
              }
              if (!['crossTab', 'table'].includes(chartType)) {
                formattedAxes.filterPanels.push(payload);
              }
            }

            if (
              chartControls?.properties[propKey]?.columnSecurity?.filterPanels[
                currentOperatorColumn
              ]?.filters.length > 0
            ) {
              const rawFilters =
                chartControls?.properties?.[propKey]?.columnSecurity?.filterPanels?.[
                  currentOperatorColumn
                ]?.filters || [];

              // const enhancedFilters = rawFilters.map((filter: any) => ({
              //   ...filter,
              //   filterType: `column_level_security_${currentOperator.toLowerCase()}`,
              //   operator: 'in',
              //   shouldExclude: false,
              // }));
              const enhancedFilters = rawFilters.map((filter: any, index: number) => {
                const calculation = getCalculationInfoByUid(filter.calculationUUID);
                return index === 1
                  ? {
                      ...filter,
                      ...(calculation && {
                        calculatedField: [
                          {
                            calculatedFieldName: calculation.name.split(' ').join(' '),
                            isAggregated: calculation.isAggregated,
                            fields: calculation.calculationInfo.fields,
                            flows: calculation.calculationInfo.flows,
                            conditionFilters: calculation.calculationInfo.conditionFilters,
                            overrided: calculation.calculationInfo.overrideCount > 0,
                          },
                        ],
                        isCalculatedField: true,
                      }),
                      filterType: 'column_level_security_column',
                      operator: 'in',
                      shouldExclude: false,
                    }
                  : {
                      ...filter,
                      ...(calculation && {
                        calculatedField: [
                          {
                            calculatedFieldName: calculation.name.split(' ').join(' '),
                            isAggregated: calculation.isAggregated,
                            fields: calculation.calculationInfo.fields,
                            flows: calculation.calculationInfo.flows,
                            conditionFilters: calculation.calculationInfo.conditionFilters,
                            overrided: calculation.calculationInfo.overrideCount > 0,
                          },
                        ],
                        isCalculatedField: true,
                      }),
                      filterType: `column_level_security_${currentOperatorColumn.toLowerCase()}`,
                      operator: 'in',
                      shouldExclude: false,
                    };
              });

              const payload = {
                filters: enhancedFilters,
                panelName: 'columnLevelSecurityFilters',
                shouldAllConditionsMatch: true,
              };
              const hasMeasureMapping =
                enhancedFilters.length > 0 &&
                enhancedFilters.find(
                  (filter: any) =>
                    filter?.filterType === 'column_level_security_column' &&
                    filter?.mapping?.length > 0,
                );
              if (hasMeasureMapping) {
                formattedAxes.filterPanels.push(payload);
              }
            }
          }
        });
      } else {
        //chartGroup
        Logger('info', 'getting reportfilkter');
        chartGroup.tabTile[propKey]?.forEach((grp: any) => {
          const dsOfFilterGroup = chartGroup.groups[grp].dataSetId;
          const _chartProp = chartProp.selectedDs ? chartProp : chartProp.properties[propKey];
          const dsIdOfTile = _chartProp?.selectedDs.id;
          if (dsOfFilterGroup !== dsIdOfTile) {
            if (setShowSnackBar && setSnackBarMessage) {
              setSnackBarMessage(`${chartGroup.groups[grp].name} is ignored.`);
              setShowSnackBar(true);
            }
            return;
          }
          let rightFilterObj = getChartLeftFilter(
            chartGroup.groups[grp].filters,
            'reportFilters',
            grp,
            chartGroup.groups[grp].syncFilter,
            chartGroup.groups[grp].any_condition_match,
          );

          if (rightFilterObj.filters.length > 0) {
            formattedAxes.filterPanels.push(rightFilterObj);
          }
        });

        if (
          chartControls?.properties[propKey]?.rowSecurity?.filterPanels[currentOperator]?.filters
            .length > 0
        ) {
          const rawFilters =
            chartControls?.properties?.[propKey]?.rowSecurity?.filterPanels?.[currentOperator]
              ?.filters || [];

          const enhancedFilters = rawFilters.map((filter: any) => {
            const calculation = getCalculationInfoByUid(filter.calculationUUID);
            return {
              ...filter,
              ...(calculation && {
                calculatedField: [
                  {
                    calculatedFieldName: calculation.name.split(' ').join(' '),
                    isAggregated: calculation.isAggregated,
                    fields: calculation.calculationInfo.fields,
                    flows: calculation.calculationInfo.flows,
                    conditionFilters: calculation.calculationInfo.conditionFilters,
                    overrided: calculation.calculationInfo.overrideCount > 0,
                  },
                ],
                isCalculatedField: true,
              }),
              filterType: `row_level_security_${currentOperator.toLowerCase()}`,
              operator: 'in',
              shouldExclude: false,
            };
          });

          const payload = {
            filters: enhancedFilters,
            panelName: 'rowLevelSecurityFilters',
            shouldAllConditionsMatch: true,
          };
          if (
            ['crossTab', 'table'].includes(chartType) &&
            chartProp.chartAxes?.[1]?.fields.length > 0
          ) {
            formattedAxes.filterPanels.push(payload);
          }
          if (!['crossTab', 'table'].includes(chartType)) {
            formattedAxes.filterPanels.push(payload);
          }
        }

        if (
          chartControls?.properties[propKey]?.columnSecurity?.filterPanels[currentOperatorColumn]
            ?.filters.length > 0
        ) {
          const rawFilters =
            chartControls?.properties?.[propKey]?.columnSecurity?.filterPanels?.[
              currentOperatorColumn
            ]?.filters || [];

          // const enhancedFilters = rawFilters.map((filter: any) => ({
          //   ...filter,
          //   filterType: `column_level_security_${currentOperator.toLowerCase()}`,
          //   operator: 'in',
          //   shouldExclude: false,
          // }));

          const enhancedFilters = rawFilters.map((filter: any, index: number) => {
            const calculation = getCalculationInfoByUid(filter.calculationUUID);
            return index === 1
              ? {
                  ...filter,
                  ...(calculation && {
                    calculatedField: [
                      {
                        calculatedFieldName: calculation.name.split(' ').join(' '),
                        isAggregated: calculation.isAggregated,
                        fields: calculation.calculationInfo.fields,
                        flows: calculation.calculationInfo.flows,
                        conditionFilters: calculation.calculationInfo.conditionFilters,
                        overrided: calculation.calculationInfo.overrideCount > 0,
                      },
                    ],
                    isCalculatedField: true,
                  }),
                  filterType: 'column_level_security_column',
                  operator: 'in',
                  shouldExclude: false,
                }
              : {
                  ...filter,
                  ...(calculation && {
                    calculatedField: [
                      {
                        calculatedFieldName: calculation.name.split(' ').join(' '),
                        isAggregated: calculation.isAggregated,
                        fields: calculation.calculationInfo.fields,
                        flows: calculation.calculationInfo.flows,
                        conditionFilters: calculation.calculationInfo.conditionFilters,
                        overrided: calculation.calculationInfo.overrideCount > 0,
                      },
                    ],
                    isCalculatedField: true,
                  }),
                  filterType: `column_level_security_${currentOperatorColumn.toLowerCase()}`,
                  operator: 'in',
                  shouldExclude: false,
                };
          });

          const payload = {
            filters: enhancedFilters,
            panelName: 'columnLevelSecurityFilters',
            shouldAllConditionsMatch: true,
          };
          const hasMeasureMapping =
            enhancedFilters.length > 0 &&
            enhancedFilters.find(
              (filter: any) =>
                filter?.filterType === 'column_level_security_column' &&
                filter?.mapping?.length > 0,
            );
          if (hasMeasureMapping) {
            formattedAxes.filterPanels.push(payload);
          }
        }
      }
    }

    return formattedAxes;
  };

  let allMeasureFields: any = axesValues.find((axis: any) => axis.name === 'Measure');

  const measureFields = Array.isArray(allMeasureFields?.fields) ? allMeasureFields.fields : [];

  measureFields.forEach((field: any, idx: number) => {
    field.measureOrder = idx + 1;
  });

  let hasMeasureOverride = allMeasureFields?.fields.filter((field: any) => {
    return field.override ? true : false;
  });

  let hasNoMeasureOverride = allMeasureFields?.fields.filter((field: any) => {
    return field.override ? false : true;
  });
  let formattedAxes: any = [];

  if (hasMeasureOverride && hasMeasureOverride.length > 0) {
    let hasNoMeasureOverrideFields = JSON.parse(JSON.stringify(hasNoMeasureOverride));

    let tempAxesValues = JSON.parse(JSON.stringify(axesValues));

    tempAxesValues.find((axis: any) => axis.name === 'Measure').fields = [];
    tempAxesValues.find((axis: any) => axis.name === 'Measure').fields = hasNoMeasureOverrideFields;

    let tempFormattedAxes = getFormattedAxes(tempAxesValues);

    if (tempFormattedAxes) {
      if (
        tempFormattedAxes.dimensions &&
        tempFormattedAxes.dimensions.length === 0 &&
        tempFormattedAxes.measures?.length === 0
      )
        tempFormattedAxes.filterPanels = [];

      formattedAxes.push(tempFormattedAxes);
    }
    hasMeasureOverride.forEach((measureField: any) => {
      let tempMeasureField = JSON.parse(JSON.stringify(measureField));
      let tempOverrideAxes = JSON.parse(JSON.stringify(tempMeasureField.override));

      delete tempMeasureField.override;

      if (chartProp.chartType === 'scatterPlot') {
        tempOverrideAxes[2].name = 'Measure';
        tempOverrideAxes[2].fields = [...tempOverrideAxes[2].fields, ...tempOverrideAxes[3].fields];
        delete tempOverrideAxes[3];
      }

      tempOverrideAxes.find((axis: any) => axis.name === 'Measure').fields = [];
      tempOverrideAxes.find((axis: any) => axis.name === 'Measure').fields.push(tempMeasureField);

      let overrideFormattedAxes: any = getFormattedAxes(tempOverrideAxes);

      overrideFormattedAxes && formattedAxes.push(overrideFormattedAxes);
    });
  } else {
    let tempFormattedAxes = getFormattedAxes(axesValues);
    if (
      tempFormattedAxes &&
      (tempFormattedAxes.dimensions?.length > 0 || tempFormattedAxes.measures?.length > 0)
    )
      formattedAxes.push(tempFormattedAxes);
  }

  let _selectedDS: any = {};

  if (chartProp.selectedDs) {
    _selectedDS = chartProp.selectedDs;
  } else {
    _selectedDS = chartProp.properties[propKey].selectedDs;
  }

  let url = '';
  if (_selectedDS.isFlatFileData) {
    url = `query?datasetid=${_selectedDS.id}`;
  } else {
    url = `query?dbconnectionid=${_selectedDS.connectionId}&datasetid=${_selectedDS.id}&workspaceId=${_selectedDS.workSpaceId}`;
  }

  /*	PRS 21/07/2022	*/
  if (formattedAxes && formattedAxes.length > 0) {
    if (savedCalculations) {
      // check whether the measure is a calculated field or not
      addCalculationMetadataToAxes(propKey, formattedAxes, savedCalculations, chartProp);
    }

    //for tooltip

    if (tooltipFormats && !['crossTab', 'table', 'simplecard', 'boxPlot'].includes(chartType)) {
      let extraMeasureField = JSON.parse(JSON.stringify(tooltipFormats?.extraMeasureField));
      const hasExtraMeasures =
        tooltipFormats && extraMeasureField && extraMeasureField[0]?.fields?.length > 0;
      //push extra measure fields in formattedAxes.measures
      if (hasExtraMeasures) {
        const regularMeasuresCount = allMeasureFields?.fields?.length || 0;
        extraMeasureField[0]?.fields.forEach((field: any, idx: number) => {
          field.measureOrder = regularMeasuresCount + idx + 1;
        });

        // Separate extra measure fields into those with and without overrides
        let extraMeasuresWithOverride: any[] = [];
        let extraMeasuresWithoutOverride: any[] = [];

        extraMeasureField[0]?.fields.forEach((field: any) => {
          if (field.override) {
            extraMeasuresWithOverride.push(field);
          } else {
            extraMeasuresWithoutOverride.push(field);
          }
        });

        // Add non-overridden extra measures to the first formattedAxes object
        if (extraMeasuresWithoutOverride.length > 0) {
          const tempExtraMeasureField = [{ name: 'Measure', fields: extraMeasuresWithoutOverride }];
          const tempFormattedAxes = getFormattedAxes(tempExtraMeasureField);
          tempFormattedAxes &&
            tempFormattedAxes.measures.forEach((measureField: any) =>
              formattedAxes[0].measures.push(measureField),
            );
        }

        // For each extra measure with override, create a separate formattedAxes object
        extraMeasuresWithOverride.forEach((measureField: any) => {
          let tempMeasureField = JSON.parse(JSON.stringify(measureField));
          let tempOverrideAxes = JSON.parse(JSON.stringify(tempMeasureField.override));

          delete tempMeasureField.override;

          if (chartProp.chartType === 'scatterPlot') {
            tempOverrideAxes[2].name = 'Measure';
            tempOverrideAxes[2].fields = [
              ...tempOverrideAxes[2].fields,
              ...tempOverrideAxes[3].fields,
            ];
            delete tempOverrideAxes[3];
          }

          tempOverrideAxes.find((axis: any) => axis.name === 'Measure').fields = [];
          tempOverrideAxes
            .find((axis: any) => axis.name === 'Measure')
            .fields.push(tempMeasureField);

          let overrideFormattedAxes: any = getFormattedAxes(tempOverrideAxes);

          overrideFormattedAxes && formattedAxes.push(overrideFormattedAxes);
        });

        if (savedCalculations) {
          addCalculationMetadataToAxes(propKey, formattedAxes, savedCalculations, chartProp);
        }
      }
    } else if (
      tooltipFormats &&
      ['crossTab', 'table', 'simplecard', 'boxPlot'].includes(chartType)
    ) {
      resetExtraMeasureField(propKey);
    }

    // console.log(formattedAxes);
    let updatedPayload: any[] = formattedAxes;
    console.log('When Payload update');
    let selectedDs = chartProp.selectedDs || chartProp.properties[propKey].selectedDs;

    let isChanged = false;
    updatedPayload = await Promise.all(
      updatedPayload.map(async (el: any) => {
        if (el.dimensions) {
          el.dimensions = await Promise.all(
            el.dimensions.map(async (dim: any) => {
              const res = await checkIfMissingColumn(
                dim,
                selectedDs,
                tablesForSelectedDataSets[selectedDs.id],
                dim.originalDataSetId !== selectedDs.id,
              );
              if (dim.originalDataSetId !== selectedDs.id) {
                dim.tableId = res.newTableId;
              } else isChanged = res.isMissing;

              if (dim.originalTableId && dim.originalDataSetId) {
                delete dim.originalTableId;
                delete dim.originalDataSetId;
              }
              return dim;
            }),
          );
        }

        if (el.filterPanels) {
          el.filterPanels = await Promise.all(
            el.filterPanels.map(async (filterPanel: any) => {
              filterPanel.filters = await Promise.all(
                filterPanel.filters.map(async (filter: any) => {
                  if (filter.isCalculatedField) {
                    const cal = savedCalculations.find(
                      (cal: any) =>
                        cal.name === filter.fieldName && cal.datasetId === selectedDs.id,
                    );
                    if (!cal) {
                      isChanged = true;
                      return filter;
                    }
                    return filter;
                  } else {
                    const res = await checkIfMissingColumn(
                      filter,
                      selectedDs,
                      tablesForSelectedDataSets[selectedDs.id],
                      filter.originalDataSetId !== selectedDs.id,
                    );
                    if (filter.originalDataSetId !== selectedDs.id) {
                      filter.tableId = res.newTableId;
                    } else isChanged = res.isMissing;

                    if (filter.originalTableId && filter.originalDataSetId) {
                      delete filter.originalTableId;
                      delete filter.originalDataSetId;
                    }
                    return filter;
                  }
                }),
              );
              return filterPanel;
            }),
          );
        }

        if (el.measures) {
          el.measures = await Promise.all(
            el.measures.map(async (measure: any) => {
              if (measure.isCalculatedField) {
                const cal = savedCalculations.find(
                  (cal: any) =>
                    cal.calculationInfo.calculatedFieldName ===
                      measure.calculatedField[0].calculatedFieldName &&
                    cal.datasetId === selectedDs.id,
                );
                if (!cal) {
                  isChanged = true;
                  return measure;
                }
                return measure;
              } else {
                const res = await checkIfMissingColumn(
                  measure,
                  selectedDs,
                  tablesForSelectedDataSets[selectedDs.id],
                  measure.originalDataSetId !== selectedDs.id,
                );
                if (measure.originalDataSetId !== selectedDs.id) {
                  measure.tableId = res.newTableId;
                } else isChanged = res.isMissing;
              }
              if (measure.originalTableId && measure.originalDataSetId) {
                delete measure.originalTableId;
                delete measure.originalDataSetId;
              }
              return measure;
            }),
          );
        }

        return el;
      }),
    );

    const message =
      'The selected dataset does not contain all the fields used in this chart. Please remove or replace the missing fields and try again.';
    if (isChanged) {
      // console.log('is Changed');
      if (
        showErrorNotification &&
        chartControls?.properties?.[propKey]?.chartErrorDetails?.statusCode !== 400 &&
        chartControls?.properties?.[propKey]?.chartErrorDetails?.message !== message
      ) {
        showErrorNotification('error', message);
      }
      const error = {
        statusCode: 400,
        message: message,
      };
      storeErrorDetails(propKey, error);
    } else {
      // console.log('Api Called getChartDataFunction');
      var res: any = await FetchData({
        requestType: 'withData',
        method: 'POST',
        url: url,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        data: updatedPayload,
      });
      if (res.status) {
        storeErrorDetails(propKey, null);
        if (res.data && res.data.length > 0) {
          if (forQueryData) {
            return updatedPayload;
          } else {
            //-------------------------------- Web API data sources return empty strings ("") when a value is missing/empty, while db and flatfile sources return null in sample records. To keep the behavior consistent across all sources(such as showing “(Blank)”) for missing/empty values, we convert empty strings from Web API responses into null before storing them.-------------------------------------//
            const tablesInSelectedDataset = tablesForSelectedDataSets?.[selectedDs?.id] ?? [];
            const isWebApiDataset = tablesInSelectedDataset.some(
              (table: any) => table.vendor === 'webapi',
            );
            if (isWebApiDataset) {
              return res.data.map((row: any) => {
                const newRow: any = {};
                for (const key in row) {
                  newRow[key] = row[key] === '' ? null : row[key];
                }
                return newRow;
              });
            }
            return res.data;
          }
        } else {
          Logger('info', 'Change filter conditions.');
        }
      } else {
        Logger('error', 'Get Table Data Error', res.error);
        storeErrorDetails(propKey, {
          statusCode: res.responseStatusCode,
          message:
            res.responseStatusCode === 422
              ? typeof res.error === 'string' && res.error.includes('Error: Table Id')
                ? 'The table and column is not available in the dataset'
                : message
              : res.error,
        });
        if (showErrorNotification) {
          showErrorNotification(
            'error',
            res.responseStatusCode === 422
              ? typeof res.error === 'string' && res.error.includes('Error: Table Id')
                ? 'The table and column is not available in the dataset'
                : message
              : 'Something went wrong while getting the chart data',
          );
        }
      }
    }
  }
};

export const isInvalidValue = (val: any) => {
  if (val === undefined || val === null || val === '') {
    return true;
  }
  return false;
};

const addCalculationMetadataToAxes = (
  propKey: string,
  formattedAxes: any,
  savedCalculations: ICurrentCalculationSession[],
  chartProp: any,
) => {
  formattedAxes.forEach((axis: any) => {
    const formattedMeasures = axis.measures;

    const formattedDimensions = axis.dimensions;

    savedCalculations &&
      formattedMeasures.forEach((measure: any) => {
        if (measure.SavedCalculationUUID) {
          const savedCalculationForThisMeasure = savedCalculations?.find(
            (item) => item.uuid === measure.SavedCalculationUUID,
          );
          if (!savedCalculationForThisMeasure) return;

          const isAgg = savedCalculationForThisMeasure.isAggregated;
          const flows = savedCalculationForThisMeasure.calculationInfo.flows;
          const chartType = chartProp.chartType ?? chartProp.properties[propKey].chartType;
          const axes = chartProp.chartAxes ?? chartProp.properties[propKey].chartAxes;
          Object.keys(flows).forEach((flowKey) => {
            flows[flowKey].forEach((flowCondition: ICalculationStep) => {
              if (
                flowCondition.flow === 'summarize' &&
                flowCondition.flowOverride?.windowFunction &&
                ['crossTab', 'heatmap'].includes(chartType)
              ) {
                const { row, column } = axes.reduce(
                  (acc: { row: number; column: number }, axis: any) => {
                    if (axis.name === 'Row') acc.row = axis.fields.length;
                    if (axis.name === 'Column') acc.column = axis.fields.length;
                    return acc;
                  },
                  { row: 0, column: 0 },
                );
                flowCondition.flowOverride.windowFunction = {
                  ...flowCondition.flowOverride.windowFunction,
                  windowFnMatrix: [row, column],
                };
              } else if (
                flowCondition.flow === 'summarize' &&
                flowCondition.flowOverride?.windowFunction
              ) {
                delete flowCondition.flowOverride?.windowFunction?.windowFnMatrix;
              }
              if (flowCondition.flowOverride?.windowFunction) {
                if (
                  !['standing', 'sliding'].includes(
                    flowCondition.flowOverride.windowFunction.windowFn[0],
                  ) &&
                  ['first', 'last'].includes(flowCondition.flowOverride.windowFunction.windowFn[1])
                ) {
                  delete flowCondition.flowOverride.windowFunction.windowFnOption;
                }
              }
            });
          });

          measure.isCalculatedField = true;
          measure.calculatedField = [
            {
              calculatedFieldName:
                savedCalculationForThisMeasure.calculationInfo.calculatedFieldName,
              // TODO: [0] make it dynamic
              isAggregated: isAgg,
              fields: savedCalculationForThisMeasure.calculationInfo.fields,
              flows: savedCalculationForThisMeasure.calculationInfo.flows,
              conditionFilters: savedCalculationForThisMeasure.calculationInfo.conditionFilters,
              overrided: savedCalculationForThisMeasure.calculationInfo.overrideCount > 0,
              // isAggregated: savedCalculationForThisMeasure.calculationInfo.flows[allSavedFlows[allSavedFlows.length - 1]][0].isAggregated,
            },
          ];
        }
      });

    savedCalculations &&
      formattedDimensions.forEach((dimension: any) => {
        if (dimension.SavedCalculationUUID) {
          const savedCalculationForThisMeasure = savedCalculations?.find(
            (item) => item.uuid === dimension.SavedCalculationUUID,
          );
          if (!savedCalculationForThisMeasure) return;

          const isAgg = savedCalculationForThisMeasure.isAggregated;
          dimension.isCalculatedField = true;

          dimension.calculatedField = [
            {
              calculatedFieldName:
                savedCalculationForThisMeasure.calculationInfo.calculatedFieldName,
              // TODO: [0] make it dynamic
              isAggregated: isAgg,
              fields: savedCalculationForThisMeasure.calculationInfo.fields,
              flows: savedCalculationForThisMeasure.calculationInfo.flows,
              conditionFilters: savedCalculationForThisMeasure.calculationInfo.conditionFilters,
              // isAggregated: savedCalculationForThisMeasure.calculationInfo.flows[allSavedFlows[allSavedFlows.length - 1]][0].isAggregated,
            },
          ];
        }
      });
  });

  const formattedAxesWhereFlowsNeedToBeSorted = formattedAxes[0];

  for (const axes in formattedAxesWhereFlowsNeedToBeSorted) {
    for (const field of formattedAxesWhereFlowsNeedToBeSorted[axes]) {
      const isCalculatedField = field.isCalculatedField;
      if (!isCalculatedField) continue;
      else {
        for (const calc of field.calculatedField) {
          const calculationWhereFlowHasToBeSorted = calc;

          const dependencyTracker: {
            [key: string]: boolean;
          } = {};

          for (const flow in calculationWhereFlowHasToBeSorted.flows) {
            dependencyTracker[flow] = false;
          }

          const sortedDependencyStack: any = [];

          let cycleDetected = false;

          const flowSorter = (flow: string, visited: Set<string>) => {
            // Early return if a cycle was already detected
            if (cycleDetected) return;

            if (sortedDependencyStack.includes(flow)) return;
            if (visited.has(flow)) {
              cycleDetected = true; // Set the flag when a cycle is detected
              return;
            }

            visited.add(flow);
            dependencyTracker[flow] = true;

            for (const condition of calculationWhereFlowHasToBeSorted.flows[flow]) {
              // Check early if cycle was detected in a recursive call
              if (cycleDetected) return;

              const sourceList = condition.source.filter(
                (source: string, index: number) => condition.sourceType[index] === 'flow',
              );

              if (condition.flow === 'IfElse') {
                const filterId = condition.filter;
                const filter = calculationWhereFlowHasToBeSorted.conditionFilters[filterId][0];

                for (const leftOperand of filter.conditions) {
                  const leftOperandType = leftOperand.leftOperandType[0];
                  if (
                    leftOperandType === 'flow' &&
                    !sourceList.includes(leftOperand.leftOperand[0])
                  ) {
                    sourceList.push(leftOperand.leftOperand[0]);
                  }

                  const rightOperandType = leftOperand.rightOperandType[0];
                  if (
                    rightOperandType === 'flow' &&
                    !sourceList.includes(leftOperand.rightOperand[0])
                  ) {
                    sourceList.push(leftOperand.rightOperand[0]);
                  }
                }
              }

              for (const source of sourceList) {
                flowSorter(source, new Set(visited)); // Pass a new set to avoid mutation issues
                // Check after each recursive call
                if (cycleDetected) return;
              }
            }

            visited.delete(flow);

            if (!sortedDependencyStack.includes(flow)) {
              sortedDependencyStack.push(flow);
            }
          };

          for (const flow in dependencyTracker) {
            if (cycleDetected) break; // Stop processing if a cycle was detected
            if (!sortedDependencyStack.includes(flow)) {
              flowSorter(flow, new Set());
            }
          }

          if (cycleDetected) {
            return;
          }

          const newSortedFlows: any = {};

          for (const sortedDependency of sortedDependencyStack) {
            newSortedFlows[sortedDependency] =
              calculationWhereFlowHasToBeSorted.flows[sortedDependency];
          }

          calc.flows = newSortedFlows;
        }
      }
    }
  }
};
